import { Formik } from "formik";
import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { toast, ToastBar, Toaster } from "react-hot-toast";
import * as yup from "yup";
import { createEMSBootAppV2, getTemplateForApplication } from "../../../api";
import pricingValidation from "../../../components/modals/add-new-application-modal/pricing-validation";
import config from "../../../config";
import Breadcrumbs from "../../../v2/components/Breadcrumbs";
import useUserStore from "../../../v2/stores/user";
import ButtonIcon from "../../components/ButtonIcon/ButtonIcon";
import Select from "../../components/form/select";
import ContactInformation from "./components/contact-info";
import Cost from "./components/cost";
import Details from "./components/details";
import Equipments from "./components/equipments";
import SendLinkMerchantModal from "./components/merchant-link-modal";
import Pricing from "./components/pricing";
import Processors from "./components/processor";
import {
  contactInfoSchema,
  detailsInfoSchema,
  equipmentSchema,
  pricingInfoSchema,
  processorInfoSchema,
  getDefaultFees,
} from "./validation-schema";
import { getDualPricingValidation } from "./utils/validation";
import styles from "./style.module.scss";

const formSchema = yup.object({
  ...contactInfoSchema.fields,
  ...pricingInfoSchema.fields,
  ...equipmentSchema,
  ...detailsInfoSchema.fields,
  ...processorInfoSchema,
});

let dual_pricing_validation = pricingValidation.pricingValidation.dual_pricing;
const surcharge_validation = pricingValidation.pricingValidation.surcharge;
const flat_rate_validation = pricingValidation.pricingValidation.flat_rate;
const ic_plus_validation = pricingValidation.pricingValidation.ic_plus;
const tiered_validation = pricingValidation.pricingValidation.tiered;

const contactInfoField = [
  "first_name",
  "last_name",
  "dba_name",
  "country",
  "dba_street",
  "dba_city",
  "dba_state",
  "dba_zip",
  "postal_code",
  "email",
  "mobile",
  "is_merchant_referral",
  "referrer_name",
  "referrer_mid",
  "is_tm",
  "telemarketer_name",
  "cold_call",
  "tm_lead",
];

const pricingInfoFields = [
  "pricing_model",
  "dual_pricing_rate",
  "dual_pricing_monthly_fee",
  "surcharge_rate",
  "surcharge_per_transaction",
  "surcharge_monthly_fee",
  "flat_rate",
  "flat_rate_transaction",
  "flat_rate_amex",
  "flat_rate_amex_transaction",
  "ic_plus_rate",
  "ic_plus_transaction_fee",
  "ic_plus_amex_rate",
  "ic_plus_amex_transaction_fee",
  "tiered_qual_rate",
  "tiered_qual_transaction",
  "tiered_qual_debit_rate",
  "tiered_qual_debit_per_transaction",
  "tiered_mid_qual_rate",
  "tiered_mid_qual_transaction",
  "tiered_mid_qual_debit_rate",
  "tiered_mid_qual_debit_per_transaction",
  "tiered_non_qual_rate",
  "tiered_non_qual_transaction",
  "tiered_non_qual_debit_rate",
  "tiered_non_qual_debit_per_transaction",
  "tiered_amex_structure",
  "amex_tier_one_rate",
  "amex_tier_one_fee",
  "amex_tier_two_rate",
  "amex_tier_two_fee",
  "amex_tier_three_rate",
  "amex_tier_three_fee",
  "amex_tier_four_rate",
  "amex_tier_four_fee",
  "tiered_amex_flat_rate",
  "tiered_amex_flat_transaction",
  "tiered_amex_ic_plus_rate",
  "tiered_amex_ic_plus_transaction",
  "pin_debit_network_fee",
  "debit_rate",
  "debit_per_txn",
  "ebt",
  "fnf_number",
  "ebt_transactions",
  "var_sheet_request",
  // "reprogram_hardware",
  // "use_existing_gateway",
  // "existing_gateway",
  "var_only",
];

const detailsInfoField = [
  "who_to_deploy_equipment",
  "who_to_pay_for_equipment",
  "who_to_deploy_gateway",
  "billing_sequence",
  "cover_cancellation",
  "reimbursement_maximum",
  "expedited_funding",
  "batch_time",
  "equipment_address",
  "invoice_address",
  // "special_instructions",
];

export const reprogrammedHardwareInitialValues = {
  equipment_type: "",
  equipment_model: "",
  serial_number: "",
  quantity: "1",
  application_type: "",
  comm_method: "ip",
  auto_batch: "no",
  auto_batch_time: "",
  special_instructions: "",
};

const CreateAppPage = ({ history, match }) => {
  const organizationId = useUserStore((state) => state.organizationId);
  const agentUrl = useUserStore((state) => state.agentUrl);
  const isPCAdmin = useUserStore((state) => state.isPCAdmin);
  const [continueURL, setContinueURL] = useState();
  const [submissionType, setSubmissionType] = useState("");
  const [sendLinkModal, setSendLinkModal] = useState(false);
  const [openSections, setOpenSections] = useState({
    contact: true,
    pricing: false,
    equipments: false,
    details: false,
    processors: isPCAdmin ? false : null,
  });
  const [templateList, setTemplateList] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);

  const defaultFees = getDefaultFees(organizationId);
  dual_pricing_validation = getDualPricingValidation(organizationId);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    dba_name: "",
    dba_street: "",
    dba_city: "",
    dba_state: "",
    dba_zip: "",
    postal_code: "",
    email: "",
    mobile: "",
    // NOTE: This is a hack/weird way to set a country; which is doubled up
    // as a form language field on the backend.
    // Set the country to lowercase 'ca' if it's Canada
    // Set the country to lowercase 'en' if it's USA
    country: match?.params?.country === "ca" ? "ca" : "en",

    // pricing fields
    pricing_model: "dual_pricing",
    // Dual pricing fields
    dual_pricing_rate: dual_pricing_validation.dual_pricing_rate.default,
    dual_pricing_monthly_fee:
      dual_pricing_validation.dual_pricing_monthly_fee.default.toFixed(2),

    // surcharge
    surcharge_rate: surcharge_validation.surcharge_rate.default.toFixed(2),
    surcharge_per_transaction:
      surcharge_validation.surcharge_per_transaction.default.toFixed(2),
    surcharge_monthly_fee:
      surcharge_validation.surcharge_monthly_fee.default.toFixed(2),

    // Flat Rate
    flat_rate: flat_rate_validation.flat_rate.default.toFixed(2),
    flat_rate_transaction:
      flat_rate_validation.flat_rate_transaction.default.toFixed(2),
    flat_rate_amex: flat_rate_validation.flat_rate_amex.default.toFixed(2),
    flat_rate_amex_transaction:
      flat_rate_validation.flat_rate_amex_transaction.default.toFixed(2),

    // IC+
    ic_plus_rate: ic_plus_validation.ic_plus_rate.default.toFixed(2),
    ic_plus_transaction_fee:
      ic_plus_validation.ic_plus_transaction_fee.default.toFixed(2),
    ic_plus_amex_rate: ic_plus_validation.ic_plus_amex_rate.default.toFixed(2),
    ic_plus_amex_transaction_fee:
      ic_plus_validation.ic_plus_amex_transaction_fee.default.toFixed(2),

    // tiered
    tiered_qual_rate: tiered_validation.tiered_qual_rate.default.toFixed(2),
    tiered_qual_transaction:
      tiered_validation.tiered_qual_transaction.default.toFixed(2),
    tiered_qual_debit_rate:
      tiered_validation.tiered_qual_debit_rate.default.toFixed(2),
    tiered_qual_debit_per_transaction:
      tiered_validation.tiered_qual_debit_per_transaction.default.toFixed(2),

    tiered_mid_qual_rate:
      tiered_validation.tiered_mid_qual_rate.default.toFixed(2),
    tiered_mid_qual_transaction:
      tiered_validation.tiered_mid_qual_transaction.default.toFixed(2),
    tiered_mid_qual_debit_rate:
      tiered_validation.tiered_mid_qual_debit_rate.default.toFixed(2),
    tiered_mid_qual_debit_per_transaction:
      tiered_validation.tiered_mid_qual_debit_per_transaction.default.toFixed(
        2,
      ),

    tiered_non_qual_rate:
      tiered_validation.tiered_non_qual_rate.default.toFixed(2),
    tiered_non_qual_transaction:
      tiered_validation.tiered_non_qual_transaction.default.toFixed(2),
    tiered_non_qual_debit_rate:
      tiered_validation.tiered_non_qual_debit_rate.default.toFixed(2),
    tiered_non_qual_debit_per_transaction:
      tiered_validation.tiered_non_qual_debit_per_transaction.default.toFixed(
        2,
      ),

    tiered_amex_structure: "flat_rate", // tiered, flat_rate, ic_plus

    amex_tier_one_rate: tiered_validation.amex_tier_one_rate.default.toFixed(2),
    amex_tier_one_fee: tiered_validation.amex_tier_one_fee.default.toFixed(2),
    amex_tier_two_rate: tiered_validation.amex_tier_two_rate.default.toFixed(2),
    amex_tier_two_fee: tiered_validation.amex_tier_two_fee.default.toFixed(2),
    amex_tier_three_rate:
      tiered_validation.amex_tier_three_rate.default.toFixed(2),
    amex_tier_three_fee:
      tiered_validation.amex_tier_three_fee.default.toFixed(2),
    amex_tier_four_rate:
      tiered_validation.amex_tier_four_rate.default.toFixed(2),
    amex_tier_four_fee: tiered_validation.amex_tier_four_fee.default.toFixed(2),

    tiered_amex_flat_rate:
      tiered_validation.tiered_amex_flat_rate.default.toFixed(2),
    tiered_amex_flat_transaction:
      tiered_validation.tiered_amex_flat_transaction.default.toFixed(2),

    tiered_amex_ic_plus_rate:
      tiered_validation.tiered_amex_ic_plus_rate.default.toFixed(2),
    tiered_amex_ic_plus_transaction:
      tiered_validation.tiered_amex_ic_plus_transaction.default.toFixed(2),

    // pin based debit
    pin_debit_network_fee: "yes",
    debit_rate: "2.00",
    debit_per_txn: "0.15",

    //Note: Below field is not used anywhere, but kept so as not to break anything.
    monthly_fee: "49.00",

    // ebt
    ebt: "no",
    fnf_number: "",

    // other default fees
    ...Object.keys(defaultFees).reduce((acc, key) => {
      acc[key] = defaultFees[key].default.toFixed(2);
      return acc;
    }, {}),

    // Merchant referral fields
    is_merchant_referral: "no",
    referrer_name: "",
    referrer_mid: "",
    is_tm: "no",
    telemarketer_name: "",
    cold_call: "no",
    tm_lead: "no",

    var_sheet_request: true,
    // NOTE: Re-enable this when we have a way to add multiple reprogrammable hardware
    // use_existing_gateway: false,
    // existing_gateway: "",
    // reprogram_hardware: false,
    // reprogrammed_hardware: [
    //   {
    //     equipment_type: "",
    //     equipment_model: "",
    //     serial_number: "",
    //     quantity: "1",
    //     application_type: "",
    //     comm_method: "ip",
    //     auto_batch: "no",
    //     auto_batch_time: "",
    //     special_instructions: "",
    //   },
    // ],

    var_only: {},

    equipments: [],
    other_gateway: "",

    // details
    who_to_deploy_equipment: "",
    who_to_pay_for_equipment: "",
    who_to_deploy_gateway: "",
    billing_sequence: "daily",
    cover_cancellation: "no",
    reimbursement_maximum: "",
    expedited_funding: "no",
    batch_time: "06:00 PM",
    equipment_address: "dba_address",
    invoice_address: "agent_address",
    special_instructions: "",

    // processor selection
    based_on_mcc: false,
    processors: [],

    // Legal Business Address
    legal_address_same_as_dba: false,
    legal_address: "",
    legal_city: "",
    legal_state: "",
    legal_zip: "",
  });

  const transformPricingFields = (values) => {
    const transformedValues = { ...values };
    const fieldsToTransform = {
      pin_debit_network_fee: `${values.pricing_model}_pin_debit_network_fee`,
      debit_rate: `${values.pricing_model}_pin_debit_rate`,
      debit_per_txn: `${values.pricing_model}_pin_debit_per_transaction`,
    };

    Object.entries(fieldsToTransform).forEach(([baseField, prefixedField]) => {
      if (values[baseField] !== undefined) {
        transformedValues[prefixedField] = values[baseField];
      }
    });

    return transformedValues;
  };

  const preSubmitCheck = (errors, values, touched) => {
    const hasContactErrors = !contactInfoSchema.isValidSync(values);
    const hasPricingErrors = pricingInfoFields.some(
      (field) => Object.hasOwn(errors, field) && Object.hasOwn(touched, field),
    );
    const hasEquipmentErrors =
      errors.equipments || values.equipments.length === 0;
    const hasDetailsErrors = !detailsInfoSchema.isValidSync(values);
    const hasProcessorErrors =
      isPCAdmin &&
      ((values.based_on_mcc === false &&
        (!values.processors || values.processors.length === 0)) ||
        values.based_on_mcc === undefined);

    setOpenSections({
      contact: hasContactErrors,
      pricing: hasPricingErrors,
      equipments: hasEquipmentErrors,
      details: hasDetailsErrors,
      processors: hasProcessorErrors,
    });
  };

  const onSubmit = (
    values,
    { setTouched, setSubmitting, errors, setErrors, touched },
  ) => {
    //Note: Touching all fields so that the errors are shown
    setTouched({});

    const transformedValues = transformPricingFields(values);
    transformedValues.submission_type =
      submissionType === "continue" ? "create_app" : submissionType;

    createEMSBootAppV2(transformedValues)
      .then((res) => {
        toast.loading("Creating application...");

        if (!res?.status) {
          toast.dismiss();
          toast.error("Failed to create application");
          console.error("Failed to create application.");
          return;
        }

        if (!res?.data?.editslug) {
          toast.dismiss();
          toast.error("Failed to create application");
          return;
        }

        toast.dismiss();
        toast.success("Application created successfully");

        const continueURLNonPrefixed = `v2/application/${agentUrl}/${res.data.editslug}`;
        setContinueURL(continueURLNonPrefixed);

        if (submissionType === "continue") {
          history.push(`/${continueURLNonPrefixed}`);
        }

        if (submissionType === "send_to_merchant") {
          setSendLinkModal(true);
        }

        if (submissionType === "create_app") {
          history.push("/v2/applications");
        }
      })
      .catch((error) => {
        console.error(error);

        if (error.status !== 422) {
          toast.error("Failed to submit application");
          return;
        }

        if (!error?.data?.errors) {
          toast.error("Failed to submit application");
          return;
        }

        const formattedErrors = {};
        Object.keys(error.data.errors).forEach((key) => {
          if (
            Array.isArray(error.data.errors[key]) &&
            error.data.errors[key].length > 0
          ) {
            formattedErrors[key] = error.data.errors[key][0];
          }
        });

        setErrors(formattedErrors);
        preSubmitCheck(formattedErrors, values, touched);

        const firstErrorKey = Object.keys(formattedErrors)[0];
        if (firstErrorKey) {
          toast.error(
            `Failed to submit application: ${formattedErrors[firstErrorKey]}`,
          );
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  useEffect(() => {
    getTemplateForApplication().then(res => {
      if (res.status) {
        setTemplateList(res.data);
        setTemplateOptions(res.data.map(item => ({
          label: item.name,
          value: item.id,
        })));
      }
    })
  }, [])


  const applyTemplate = (value) => {
    const template = templateList.find(item => item.id === value);
    if (template) {
      setFormData({
        ...formData,
        ...template.data,
      });
    }
  }

  return (
    <div className={styles.page}>
      <Breadcrumbs
        items={[
          { label: "Application", to: "/v2/applications" },
          { label: "Create application" },
        ]}
      />

      <h1 className={styles.title}>Create app</h1>
      <Formik
        key={match?.params?.country}
        enableReinitialize
        validationSchema={formSchema}
        onSubmit={onSubmit}
        initialValues={formData}
        validateOnBlur={true}
        validateOnChange={true}
        validationContext={{ isPCAdmin, organizationId }}
        onChange={(values) => {
          setFormData(values);
        }}
      >
        {({ handleSubmit, values, errors, touched, isSubmitting, setFieldValue }) => {
          // Update formData whenever values change
          useEffect(() => {
            setFormData(values);
          }, [values]);

          return (
            <div className={styles.content}>
              <div className={styles.left} >
                <form className={styles.form}>
                  <ContactInformation
                    isOpen={openSections.contact}
                    onToggle={() =>
                      setOpenSections((prev) => ({
                        ...prev,
                        contact: !prev.contact,
                      }))
                    }
                  />

                  <div className={styles.template_section}>
                    <div className={styles.text}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M8 1.5C6.71442 1.5 5.45772 1.88122 4.3888 2.59545C3.31988 3.30968 2.48676 4.32484 1.99479 5.51256C1.50282 6.70028 1.37409 8.00721 1.6249 9.26809C1.8757 10.529 2.49477 11.6872 3.40381 12.5962C4.31285 13.5052 5.47104 14.1243 6.73192 14.3751C7.99279 14.6259 9.29973 14.4972 10.4874 14.0052C11.6752 13.5132 12.6903 12.6801 13.4046 11.6112C14.1188 10.5423 14.5 9.28558 14.5 8C14.4982 6.27665 13.8128 4.62441 12.5942 3.40582C11.3756 2.18722 9.72335 1.50182 8 1.5ZM7.75 4.5C7.89834 4.5 8.04334 4.54399 8.16668 4.6264C8.29002 4.70881 8.38615 4.82594 8.44291 4.96299C8.49968 5.10003 8.51453 5.25083 8.48559 5.39632C8.45665 5.5418 8.38522 5.67544 8.28033 5.78033C8.17544 5.88522 8.04181 5.95665 7.89632 5.98559C7.75083 6.01453 7.60003 5.99968 7.46299 5.94291C7.32595 5.88614 7.20881 5.79001 7.1264 5.66668C7.04399 5.54334 7 5.39834 7 5.25C7 5.05109 7.07902 4.86032 7.21967 4.71967C7.36032 4.57902 7.55109 4.5 7.75 4.5ZM8.5 11.5C8.23479 11.5 7.98043 11.3946 7.7929 11.2071C7.60536 11.0196 7.5 10.7652 7.5 10.5V8C7.36739 8 7.24022 7.94732 7.14645 7.85355C7.05268 7.75979 7 7.63261 7 7.5C7 7.36739 7.05268 7.24021 7.14645 7.14645C7.24022 7.05268 7.36739 7 7.5 7C7.76522 7 8.01957 7.10536 8.20711 7.29289C8.39465 7.48043 8.5 7.73478 8.5 8V10.5C8.63261 10.5 8.75979 10.5527 8.85356 10.6464C8.94732 10.7402 9 10.8674 9 11C9 11.1326 8.94732 11.2598 8.85356 11.3536C8.75979 11.4473 8.63261 11.5 8.5 11.5Z"
                          fill="#626168"
                        />
                      </svg>
                      <span>
                        Speed up the app by using a Pricing + Equipment template
                      </span>
                    </div>
                    <div className={styles.right}>
                      <Select
                        label=""
                        placeholder="Apply Template"
                        id="apply_template"
                        name="apply_template"
                        options={templateOptions}
                        search
                        // value={values?.type}
                        onChange={applyTemplate}
                      />
                    </div>
                  </div>

                  <Pricing
                    pricingInfoFields={pricingInfoFields}
                    isOpen={openSections.pricing}
                    onToggle={() =>
                      setOpenSections((prev) => ({
                        ...prev,
                        pricing: !prev.pricing,
                      }))
                    }
                  />

                  <Equipments
                    isOpen={openSections.equipments}
                    onToggle={() =>
                      setOpenSections((prev) => ({
                        ...prev,
                        equipments: !prev.equipments,
                      }))
                    }
                  />

                  <Details
                    detailsInfoField={detailsInfoField}
                    isOpen={openSections.details}
                    onToggle={() =>
                      setOpenSections((prev) => ({
                        ...prev,
                        details: !prev.details,
                      }))
                    }
                  />

                  {isPCAdmin && (
                    <Processors
                      isOpen={openSections.processors}
                      onToggle={() =>
                        setOpenSections((prev) => ({
                          ...prev,
                          processors: !prev.processors,
                        }))
                      }
                    />
                  )}

                  <div className={styles.buttons}>
                    <ButtonIcon
                      bgColor={"#FFF"}
                      textColor="#254FE2"
                      onClick={() => {
                        setSubmissionType("create_app");
                        preSubmitCheck(errors, values, touched);
                        handleSubmit();
                      }}
                      disabled={isSubmitting}
                    >
                      Save
                    </ButtonIcon>

                    <ButtonIcon
                      bgColor={"#EDEDF4"}
                      textColor="#050506"
                      onClick={() => {
                        setSubmissionType("continue");
                        preSubmitCheck(errors, values, touched);
                        handleSubmit();
                      }}
                      disabled={isSubmitting || Object.keys(errors).length > 0}
                    >
                      Continue
                    </ButtonIcon>

                    <ButtonIcon
                      bgColor={"#4C70EF"}
                      textColor="#FFFFFF"
                      onClick={() => {
                        setSubmissionType("send_to_merchant");
                        preSubmitCheck(errors, values, touched);
                        handleSubmit();
                      }}
                      disabled={isSubmitting || Object.keys(errors).length > 0}
                    >
                      Send to Merchant
                    </ButtonIcon>
                  </div>
                </form>
              </div>
              <div className={styles.right}>
                <Cost />
              </div>

              <SendLinkMerchantModal
                onClose={() => setSendLinkModal(false)}
                isOpen={sendLinkModal}
                onContinue={() => {
                  history.push(`/${continueURL}`);
                }}
                applicationURL={`${config.paymentcloud.STRATUS_URL}${continueURL}`}
              />
            </div>
          );
        }}
      </Formik>

      {/* Toaster global component */}
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            borderRadius: "8px",
            background: "rgba(40, 39, 45, 0.85)",
            color: "#fff",
            padding: "16px 24px",
          },
          duration: 5000,
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <>
                {icon}
                {message}
                <button
                  className={styles.toast_dismissal_button}
                  onClick={() => toast.dismiss(t.id)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M12.8535 12.1462C12.9 12.1927 12.9368 12.2478 12.962 12.3085C12.9871 12.3692 13.0001 12.4343 13.0001 12.5C13.0001 12.5657 12.9871 12.6307 12.962 12.6914C12.9368 12.7521 12.9 12.8073 12.8535 12.8537C12.8071 12.9002 12.7519 12.937 12.6912 12.9622C12.6305 12.9873 12.5655 13.0003 12.4998 13.0003C12.4341 13.0003 12.369 12.9873 12.3083 12.9622C12.2476 12.937 12.1925 12.9002 12.146 12.8537L7.99979 8.70685L3.85354 12.8537C3.75972 12.9475 3.63247 13.0003 3.49979 13.0003C3.36711 13.0003 3.23986 12.9475 3.14604 12.8537C3.05222 12.7599 2.99951 12.6327 2.99951 12.5C2.99951 12.3673 3.05222 12.24 3.14604 12.1462L7.29291 7.99997L3.14604 3.85372C3.05222 3.7599 2.99951 3.63265 2.99951 3.49997C2.99951 3.36729 3.05222 3.24004 3.14604 3.14622C3.23986 3.0524 3.36711 2.99969 3.49979 2.99969C3.63247 2.99969 3.75972 3.0524 3.85354 3.14622L7.99979 7.2931L12.146 3.14622C12.2399 3.0524 12.3671 2.99969 12.4998 2.99969C12.6325 2.99969 12.7597 3.0524 12.8535 3.14622C12.9474 3.24004 13.0001 3.36729 13.0001 3.49997C13.0001 3.63265 12.9474 3.7599 12.8535 3.85372L8.70666 7.99997L12.8535 12.1462Z"
                      fill="white"
                    />
                  </svg>
                </button>
              </>
            )}
          </ToastBar>
        )}
      </Toaster>
    </div>
  );
};

export default withRouter(CreateAppPage);
